import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import V2Header from '../../components/v2/layout/V2Header';
import V2Container from '../../components/v2/layout/V2Container';
import V2Button from '../../components/v2/forms/V2Button';
import V2Input from '../../components/v2/forms/V2Input';
import V2Select from '../../components/v2/forms/V2Select';
import V2Textarea from '../../components/v2/forms/V2Textarea';
import V2Badge from '../../components/v2/ui/V2Badge';
import '../../v2-theme.css';
import './WorkerProfile.css';

/**
 * Worker Profile Page
 * Matches worker-profile.html exactly
 * Features: Profile editing, subscription management, feature gating
 */
const WorkerProfile = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const [workerProfile, setWorkerProfile] = useState(null);
  const [personalForm, setPersonalForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    country: '',
    commPreferences: { email: false, sms: false }
  });
  const [experienceForm, setExperienceForm] = useState({
    experience: '',
    positions: [],
    availability: []
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isProfessional, setIsProfessional] = useState(false);
  const [adminGrant, setAdminGrant] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const POSITIONS = [
    { value: 'dishwasher', label: 'Dishwasher' },
    { value: 'prep-cook', label: 'Prep Cook' },
    { value: 'line-cook', label: 'Line Cook' },
    { value: 'sous-chef', label: 'Sous Chef' },
    { value: 'head-chef', label: 'Head Chef' },
    { value: 'event-cook', label: 'Event Cook' },
    { value: 'banquet', label: 'Banquet' },
    { value: 'bartender', label: 'Bartender' },
    { value: 'barback', label: 'Barback' }
  ];

  const AVAILABILITY = [
    { value: 'weekday-am', label: 'Weekday AM (Breakfast/Brunch)' },
    { value: 'weekday-pm', label: 'Weekday PM (Lunch/Dinner)' },
    { value: 'weekend-am', label: 'Weekend AM (Breakfast/Brunch)' },
    { value: 'weekend-pm', label: 'Weekend PM (Lunch/Dinner)' },
    { value: 'late-night', label: 'Late Night' }
  ];

  const EXPERIENCE_OPTIONS = [
    { value: '', label: 'Choose One' },
    { value: '0-1', label: 'Less than 1 year' },
    { value: '1-3', label: '1-3 years' },
    { value: '3-5', label: '3-5 years' },
    { value: '5-10', label: '5-10 years' },
    { value: '10+', label: '10+ years' }
  ];

  // Fetch worker profile
  const fetchWorkerProfile = async () => {
    try {
      const response = await api.get(`/profiles/worker/${user.id}`);
      const profile = response.data;
      setWorkerProfile(profile);

      // Populate forms
      setPersonalForm({
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        email: user.email || '',
        phone: profile.phone || '',
        city: profile.city || '',
        state: profile.state || '',
        country: profile.country || '',
        commPreferences: profile.communication_preferences || { email: false, sms: false }
      });

      setExperienceForm({
        experience: profile.years_experience || '',
        positions: profile.positions || [],
        availability: profile.availability || []
      });

      setIsProfessional(profile.subscription_tier === 'professional');
      setAdminGrant(profile.admin_grant_professional || false);
    } catch (err) {
      console.error('Error fetching worker profile:', err);
      setError('Failed to load profile data');
    }
  };

  // Handle personal form changes
  const handlePersonalChange = (name, value) => {
    setPersonalForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCommPrefChange = (pref) => {
    setPersonalForm(prev => ({
      ...prev,
      commPreferences: {
        ...prev.commPreferences,
        [pref]: !prev.commPreferences[pref]
      }
    }));
  };

  // Handle experience form changes
  const handleExperienceChange = (name, value) => {
    setExperienceForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePositionChange = (position) => {
    setExperienceForm(prev => {
      const newPositions = prev.positions.includes(position)
        ? prev.positions.filter(p => p !== position)
        : [...prev.positions, position];

      // Free account: limit to 1 position (unless admin granted or professional)
      if (!isProfessional && !adminGrant && newPositions.length > 1) {
        alert('Free accounts can only select one position. Upgrade to Professional ($9.99/month or $99/year) to select multiple positions.');
        return prev;
      }

      return { ...prev, positions: newPositions };
    });
  };

  const handleAvailabilityChange = (avail) => {
    setExperienceForm(prev => ({
      ...prev,
      availability: prev.availability.includes(avail)
        ? prev.availability.filter(a => a !== avail)
        : [...prev.availability, avail]
    }));
  };

  // Handle password form changes
  const handlePasswordChange = (name, value) => {
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  // Save personal information
  const handlePersonalSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!personalForm.commPreferences.sms) {
      alert('Text messaging is required to accept shifts');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/profiles/worker/${user.id}`, {
        first_name: personalForm.firstName,
        last_name: personalForm.lastName,
        phone: personalForm.phone,
        city: personalForm.city,
        state: personalForm.state,
        country: personalForm.country,
        communication_preferences: personalForm.commPreferences
      });

      alert('Personal information updated successfully!');
    } catch (err) {
      console.error('Error updating personal info:', err);
      setError(err.response?.data?.error || 'Failed to update personal information');
    } finally {
      setLoading(false);
    }
  };

  // Save work experience
  const handleExperienceSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!experienceForm.experience) {
      alert('Please select your years of experience');
      return;
    }

    if (experienceForm.positions.length === 0) {
      alert('Please select at least one position');
      return;
    }

    if (experienceForm.availability.length === 0) {
      alert('Please select at least one availability option');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/profiles/worker/${user.id}`, {
        years_experience: experienceForm.experience,
        positions: experienceForm.positions,
        availability: experienceForm.availability
      });

      alert('Work experience updated successfully!');
    } catch (err) {
      console.error('Error updating experience:', err);
      setError(err.response?.data?.error || 'Failed to update work experience');
    } finally {
      setLoading(false);
    }
  };

  // Update password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (passwordForm.newPassword.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });

      alert('Password updated successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      console.error('Error updating password:', err);
      alert(err.response?.data?.error || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  // Upgrade to professional
  const handleUpgrade = () => {
    navigate('/checkout/worker-pro');
  };

  // Admin override toggle
  const handleAdminOverride = async () => {
    const newGrant = !adminGrant;
    try {
      await api.post(`/admin/grant-professional/${user.id}`, {
        grant: newGrant
      });
      setAdminGrant(newGrant);
      alert(newGrant
        ? 'Professional features granted to this Free account.\n\nThis override is for promotional/demo purposes only.'
        : 'Professional features removed. Account reverted to standard Free features.');
      await fetchWorkerProfile();
    } catch (err) {
      console.error('Error toggling admin override:', err);
      alert('Failed to update admin override');
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (user) {
      fetchWorkerProfile();
    }
  }, [user]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const hasProfessionalFeatures = isProfessional || adminGrant;

  return (
    <div className="worker-profile-page">
      <V2Header
        title="Hands On The Fly"
        userName={user?.first_name || 'Worker'}
        userType="worker"
        showProfileLink={false}
        onLogout={handleLogout}
      >
        <Link to="/dashboard/worker" className="nav-link-header">Dashboard</Link>
      </V2Header>

      <V2Container maxWidth="md">
        <div className="page-header">
          <h2 className="v2-heading-xl">Worker Profile</h2>
          <p className="v2-text-silver">Manage your account settings</p>
        </div>

        {error && <div className="v2-error-message">{error}</div>}

        {/* Personal Information */}
        <div className="profile-section">
          <h3 className="profile-section-title">Personal Information</h3>

          <form onSubmit={handlePersonalSubmit}>
            {/* Professional Features (Gated) */}
            <div className="professional-feature-box">
              <div className="feature-header">
                <div>
                  <label>Profile Photo</label>
                  <div className="feature-description">Professional Plan feature</div>
                </div>
                <V2Badge variant="secondary" size="sm">Professional Only</V2Badge>
              </div>
              <input
                type="file"
                accept="image/*"
                disabled={!hasProfessionalFeatures}
                className="file-input-disabled"
              />
              <p className="feature-note">
                Upgrade to Professional to add your photo and increase visibility to restaurants
              </p>
            </div>

            <div className="professional-feature-box">
              <div className="feature-header">
                <div>
                  <label>Professional Description</label>
                  <div className="feature-description">Tell restaurants about your experience and skills</div>
                </div>
                <V2Badge variant="secondary" size="sm">Professional Only</V2Badge>
              </div>
              <V2Textarea
                placeholder="Describe your culinary background, specialties, achievements, and what makes you stand out..."
                disabled={!hasProfessionalFeatures}
                rows={4}
              />
              <p className="feature-note">
                Upgrade to Professional to add a custom description and stand out from other candidates
              </p>
            </div>

            <div className="professional-feature-box">
              <div className="feature-header">
                <div>
                  <label>CV/Resume Upload</label>
                  <div className="feature-description">Upload your resume for restaurants to review</div>
                </div>
                <V2Badge variant="secondary" size="sm">Professional Only</V2Badge>
              </div>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                disabled={!hasProfessionalFeatures}
                className="file-input-disabled"
              />
              <p className="feature-note">
                Upgrade to Professional to upload your CV/resume (PDF, DOC, DOCX - max 5MB)
              </p>
            </div>

            <div className="form-row">
              <V2Input
                label="First Name"
                value={personalForm.firstName}
                onChange={(e) => handlePersonalChange('firstName', e.target.value)}
                required
                disabled={loading}
              />
              <V2Input
                label="Last Name"
                value={personalForm.lastName}
                onChange={(e) => handlePersonalChange('lastName', e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <V2Input
              label="Email"
              type="email"
              value={personalForm.email}
              onChange={(e) => handlePersonalChange('email', e.target.value)}
              required
              disabled={loading}
            />

            <V2Input
              label="Phone Number"
              type="tel"
              value={personalForm.phone}
              onChange={(e) => handlePersonalChange('phone', e.target.value)}
              required
              disabled={loading}
            />

            <div className="comm-pref-section">
              <p className="comm-pref-header">
                I agree to receive communications from Hands On The Fly via:
              </p>
              <div className="checkbox-group">
                <label className="checkbox-label-small">
                  <input
                    type="checkbox"
                    checked={personalForm.commPreferences.email}
                    onChange={() => handleCommPrefChange('email')}
                    disabled={loading}
                  />
                  <span>Email (Optional)</span>
                </label>
                <label className="checkbox-label-small">
                  <input
                    type="checkbox"
                    checked={personalForm.commPreferences.sms}
                    onChange={() => handleCommPrefChange('sms')}
                    disabled={loading}
                  />
                  <span>Text Message (Required)</span>
                </label>
              </div>
              <p className="feature-note">Text messaging required to accept shifts</p>
            </div>

            <div className="form-row">
              <V2Input
                label="City"
                value={personalForm.city}
                onChange={(e) => handlePersonalChange('city', e.target.value)}
                required
                disabled={loading}
              />
              <V2Input
                label="State/Province/Region"
                value={personalForm.state}
                onChange={(e) => handlePersonalChange('state', e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <V2Input
              label="Country"
              value={personalForm.country}
              onChange={(e) => handlePersonalChange('country', e.target.value)}
              required
              disabled={loading}
            />

            <V2Button type="submit" variant="secondary" fullWidth disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </V2Button>
          </form>
        </div>

        {/* Work Experience */}
        <div className="profile-section">
          <h3 className="profile-section-title">Work Experience</h3>

          <form onSubmit={handleExperienceSubmit}>
            <V2Select
              label="Years of Experience"
              value={experienceForm.experience}
              onChange={(e) => handleExperienceChange('experience', e.target.value)}
              options={EXPERIENCE_OPTIONS}
              required
              disabled={loading}
            />

            <div className="professional-feature-box" style={{ marginTop: '25px' }}>
              <div className="feature-header">
                <label className="checkbox-label">
                  <input type="checkbox" disabled={!hasProfessionalFeatures} />
                  <span>ServSafe Certification (or equivalent)</span>
                </label>
                <V2Badge variant="secondary" size="sm">Professional Only</V2Badge>
              </div>
              <p className="feature-note">
                Upgrade to Professional to add certifications and increase your visibility
              </p>
            </div>

            <div style={{ marginTop: '25px' }}>
              <p className="section-label">Positions (Select all that apply)</p>
              <p className="feature-note" style={{ marginTop: 0, marginBottom: '10px' }}>
                Free account: One position only. Upgrade to Professional for multiple positions.
              </p>
              <div className="checkbox-group">
                {POSITIONS.map(pos => (
                  <label key={pos.value} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={experienceForm.positions.includes(pos.value)}
                      onChange={() => handlePositionChange(pos.value)}
                      disabled={loading}
                    />
                    <span>{pos.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '25px' }}>
              <p className="section-label">Availability (Select all that apply)</p>
              <div className="checkbox-group">
                {AVAILABILITY.map(avail => (
                  <label key={avail.value} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={experienceForm.availability.includes(avail.value)}
                      onChange={() => handleAvailabilityChange(avail.value)}
                      disabled={loading}
                    />
                    <span>{avail.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <V2Button type="submit" variant="secondary" fullWidth disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </V2Button>
          </form>
        </div>

        {/* Change Password */}
        <div className="profile-section">
          <h3 className="profile-section-title">Change Password</h3>

          <form onSubmit={handlePasswordSubmit}>
            <V2Input
              label="Current Password"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
              required
              disabled={loading}
            />

            <V2Input
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              required
              minLength="8"
              disabled={loading}
            />
            <p className="feature-note">Minimum 8 characters</p>

            <V2Input
              label="Confirm New Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              required
              minLength="8"
              disabled={loading}
            />

            <V2Button type="submit" variant="secondary" fullWidth disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </V2Button>
          </form>
        </div>

        {/* Subscription */}
        <div className="profile-section">
          <h3 className="profile-section-title">Subscription</h3>

          <div className="subscription-status">
            <div>
              <div className="current-plan">
                Current Plan: <strong>{isProfessional ? 'Professional' : 'Free'}</strong>
              </div>
              <div className="plan-description">
                {isProfessional ? 'Full access to all features' : 'Basic features'}
              </div>
            </div>
          </div>

          <div className="subscription-grid">
            {/* Free Plan */}
            <div className="plan-card plan-card-current">
              <div className="plan-name">FREE</div>
              <div className="plan-price">$0<span className="plan-period">/month</span></div>
              <ul className="plan-features">
                <li>✓ Browse shifts</li>
                <li>✓ Basic profile</li>
                <li>✓ Claim shifts</li>
                <li>✓ Limit 1 worker role</li>
              </ul>
              <button className="plan-btn-current" disabled>Current Plan</button>
            </div>

            {/* Professional Plan */}
            <div className="plan-card plan-card-professional">
              <div className="plan-name">PROFESSIONAL</div>
              <div className="plan-price">$9.99<span className="plan-period">/month</span></div>
              <div className="plan-yearly">
                or $99<span className="yearly-period">/year</span>{' '}
                <V2Badge variant="success" size="sm">SAVE $20</V2Badge>
              </div>
              <ul className="plan-features">
                <li>✓ Everything in Free</li>
                <li>✓ Multiple worker roles</li>
                <li>✓ Profile photo</li>
                <li>✓ Professional description</li>
                <li>✓ CV/Resume upload</li>
                <li>✓ Display ServSafe certification</li>
                <li>✓ Priority visibility</li>
                <li>✓ Advanced matching</li>
              </ul>
              <V2Button variant="secondary" fullWidth onClick={handleUpgrade}>
                Upgrade
              </V2Button>
            </div>
          </div>

          {/* Admin Override (for admin users only) */}
          {user?.user_type === 'admin' && (
            <div className="admin-override-box">
              <div className="admin-override-header">⚠ Admin Controls</div>
              <div className="admin-override-description">
                Grant Professional features to this Free account for promotions, demos, or special campaigns.
              </div>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={adminGrant}
                  onChange={handleAdminOverride}
                />
                <span>Grant Professional Features (Free account remains)</span>
              </label>
              <V2Button
                variant="steel"
                fullWidth
                onClick={handleAdminOverride}
                className="admin-override-btn"
              >
                Save Admin Override
              </V2Button>
            </div>
          )}
        </div>
      </V2Container>
    </div>
  );
};

export default WorkerProfile;
